import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Globe, ExternalLink, HardDrive, Settings } from 'lucide-react';

interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  status: string;
  domains: string[];
  plan: string;
  storage_used_bytes: number;
  created_at: string;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function TenantSites() {
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<TenantInfo[]>('/tenants/mine')
      .then(setTenants)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mijn websites</h1>
        <p className="text-sm text-gray-500 mt-1">Overzicht van al je Occiput websites</p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="card p-6">
              <div className="h-5 bg-gray-100 rounded animate-pulse w-48 mb-3" />
              <div className="h-4 bg-gray-100 rounded animate-pulse w-72" />
            </div>
          ))}
        </div>
      ) : tenants.length === 0 ? (
        <div className="card p-8 text-center">
          <Globe className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-sm font-semibold text-gray-900">Nog geen websites</h3>
          <p className="text-sm text-gray-500 mt-1">Je website wordt aangemaakt zodra je aanvraag is goedgekeurd.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {tenants.map(t => (
            <div key={t.id} className="card p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-brand-500" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{t.name}</h3>
                    <p className="text-sm text-gray-500">{t.domains?.[0] || `${t.slug}`}</p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        t.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>{t.status}</span>
                      <span className="text-xs text-gray-400 capitalize">{t.plan} plan</span>
                      <span className="flex items-center gap-1 text-xs text-gray-400">
                        <HardDrive className="w-3 h-3" /> {formatBytes(t.storage_used_bytes || 0)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {t.status === 'active' && t.domains?.[0] && (
                    <>
                      <a
                        href={`https://${t.domains[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" /> Bekijk site
                      </a>
                      <a
                        href={`https://${t.domains[0]}/admin`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Settings className="w-4 h-4" /> Admin
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
